<template>
  <div class="min-h-screen bg-gray-100 flex justify-center p-4 max-w-2xl mx-auto">
    <div
      v-if="vendorW9Request.isPending || vendorW9RequestAttachments.isPending"
      class="flex flex-col mt-12"
    >
      <client-spinner />
    </div>

    <div
      v-else-if="vendorW9Request.isError || vendorW9RequestAttachments.isError"
      class="flex flex-col mt-12"
    >
      <template v-if="vendorW9Request.error?.status === 404">
        <h1 class="text-3xl font-semibold">This W-9 request is no longer available</h1>
        <p class="">{{ vendorW9Request.error?.response?.data?.error }}</p>
      </template>
      <template v-else>
        <!-- TODO kdn: if we're no longer including the reason in the response, how do we generate the heading text? Context: if all status codes are 403, it's unclear what the heading text should be.-->
        <h1 class="text-3xl font-semibold text-red-600">
          {{ vendorW9Request.error?.response?.data?.reason ?? "Something went wrong" }}
        </h1>
        <p class="text-red-600">
          {{
            vendorW9Request.error?.response?.data?.error ??
            vendorW9Request.error?.response?.data?.message ??
            "Request couldn't be completed."
          }}
        </p>
      </template>

      <hr class="my-8" />
    </div>

    <template v-else-if="!!attachment">
      <div class="text-center mt-12">
        <div class="flex justify-center mb-6">
          <div class="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center">
            <ion-icon
              name="checkmark-outline"
              class="text-white size-8"
            ></ion-icon>
          </div>
        </div>

        <h2 class="text-3xl font-bold text-gray-800 mb-2">Woohoo! You're all set</h2>
        <p class="text-gray-700 mb-8">
          We've submitted your W-9 documents for {{ vendorW9Request.data?.client_name }}.
        </p>
        <div class="bg-white rounded-lg border border-gray-200 p-4 max-w-md mx-auto">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="w-8 h-8 flex items-center justify-center">
                <file-icon :extension="attachment.type" />
              </div>

              <div class="text-left">
                <p class="text-base md:text-sm text-gray-900">{{ attachment.name }}</p>
                <p class="text-sm md:text-xs text-gray-500">W-9</p>
              </div>
            </div>

            <template v-if="deleteAttachmentMutation.isPending">
              <client-spinner />
            </template>
            <template v-else>
              <button
                type="button"
                class="p-2 text-gray-400 hover:text-red-500 transition-colors"
                @click="handleDeleteDocument"
                aria-label="Delete W-9 document"
              >
                <ion-icon
                  name="trash-outline"
                  class="size-5"
                ></ion-icon>
              </button>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="w-full max-w-2xl">
        <h1 class="text-3xl font-semibold text-gray-800 mt-[1.5em]">Submit your W-9</h1>

        <p class="text-lg md:text-base text-gray-700">
          Please upload your completed W-9 for {{ vendorW9Request.data?.vendor_name }}.
          <a
            href="https://www.irs.gov/pub/irs-pdf/fw9.pdf"
            target="_blank"
            rel="noopener noreferrer"
            class="text-green-600 underline hover:text-green-700"
          >
            Download fillable W-9
          </a>
        </p>

        <hr class="my-8" />

        <div class="bg-white rounded-lg shadow-sm p-4">
          <!-- TODO kdn: should we restrict file types or just let the upload any file even though the API only accepts PDF, PNG, JPEG, and JPG -->
          <input
            ref="fileInput"
            type="file"
            accept=".png,.jpg,.jpeg,.pdf"
            @change="handleFileSelect"
            class="hidden"
            aria-label="Drag & drop your W-9 file"
          />
          <drop-zone
            class="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-gray-400 transition-colors cursor-pointer bg-gray-100"
            @click="handleFileClick"
            @drop="handleFileDrop"
          >
            <img
              src="/img/file-icons/file-add.svg"
              alt="Upload file"
              class="size-7 mx-auto text-gray-400"
            />
            <h2 class="text-lg font-semibold text-gray-700 mt-[1em]">Drag & drop your W-9 file</h2>
            <p class="text-sm text-gray-500 mb-6">Supports PNG, JPEG, PDF and (max 50 MB)</p>
            <button
              type="button"
              :disabled="uploadW9Mutation.isPending"
              class="inline-flex items-center gap-1 px-4 py-2 border border-green-600 rounded-md text-green-600 hover:bg-green-50 transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed text-lg md:text-base"
              @click.stop="handleFileClick"
            >
              <img
                src="/img/file-icons/upload.svg"
                alt="Upload file"
                class="size-6 mx-auto text-gray-400"
              />
              {{ uploadW9Mutation.isPending ? "Uploading..." : "Upload" }}
            </button>
          </drop-zone>
        </div>

        <div
          v-if="hasUploadError"
          class="mt-4 p-4 bg-red-50 border border-red-600 rounded-lg"
        >
          <div class="flex items-center">
            <ion-icon
              name="warning-outline"
              class="w-5 h-5 text-red-600 mr-3 flex-shrink-0"
            ></ion-icon>
            <div>
              <p class="text-base md:text-sm text-gray-900 font-bold">
                <span class="text-red-600">Failed to upload.</span>
                {{ fileProcessingError ?? uploadW9Mutation.error?.response?.data?.message }}
              </p>
              <p class="text-base md:text-sm text-gray-900">{{ selectedFile.name }}</p>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-center text-sm text-gray-500 mt-[2em]">
          <svg
            class="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            ></path>
          </svg>
          <span>
            <a
              href="https://financial-cents.com/"
              target="_blank"
              rel="noopener noreferrer"
              class="underline hover:text-green-600"
              >Financial Cents</a
            >
            uses secure, encrypted connections to protect all uploaded documents.
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, reactive } from "vue"
import { useRoute } from "vue-router"
import DropZone from "../../../js/components/DropZone.vue"
import ClientSpinner from "../../ClientSpinner.vue"
import VendorPortal from "../../../js/repository/VendorPortal"
import Form from "@/helpers/Form"
import Confetti from "../../../js/helpers/Confetti"
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query"
import { useGlobalMessaging } from "../../../js/composables/useGlobalMessaging"
import FileIcon from "../../components/FileIcon.vue"

const { confirm, alert } = useGlobalMessaging()
const route = useRoute()

const form = ref(
  new Form({
    document: null,
    access_token: route.query.access_token,
  }),
)

const selectedFile = ref(null)
const fileInput = ref(null)

const fileProcessingError = ref(null)

const queryClient = useQueryClient()

const vendorRequest = computed(() => route.params.vendor_request)
const token = computed(() => route.query.access_token)

const attachment = computed(() => {
  return vendorW9RequestAttachments.data?.attachment || uploadW9Mutation.data?.data?.data
})

const hasUploadError = computed(() => {
  return !!fileProcessingError.value || uploadW9Mutation.isError
})

const vendorW9Request = reactive(
  useQuery({
    queryKey: ["vendor", "w9Request", vendorRequest],
    queryFn: () => VendorPortal.getW9Request(vendorRequest.value, token.value),
    select: (data) => {
      const { vendor, company, client } = data.data.data
      return {
        vendor_name: vendor.name,
        company_name: company.name,
        client_name: client.name,
      }
    },
    staleTime: Infinity,
    retry: false,
  }),
)

const vendorW9RequestAttachments = reactive(
  useQuery({
    queryKey: ["vendor", "w9Request", vendorRequest, "attachments"],
    queryFn: () => VendorPortal.getW9RequestAttachments(vendorRequest.value, token.value),
    select: (data) => {
      const [attachment] = data.data.data
      return {
        attachment,
      }
    },
    staleTime: Infinity,
    retry: false,
  }),
)

const uploadW9Mutation = reactive(
  useMutation({
    mutationKey: ["upload-w9"],
    mutationFn: () => VendorPortal.uploadW9(vendorRequest.value, token.value, form.value),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: ["vendor", "w9Request", vendorRequest] })])
    },
    onError: (error) => {
      console.error("Upload error:", error)
    },
  }),
)

const deleteAttachmentMutation = reactive(
  useMutation({
    mutationKey: ["delete-attachment"],
    mutationFn: (attachmentId) => VendorPortal.deleteW9(vendorRequest.value, token.value, attachmentId),
    onSuccess: async () => {
      uploadW9Mutation.reset()
      await queryClient.invalidateQueries({ queryKey: ["vendor", "w9Request", vendorRequest] })
    },
    onError: (error) => {
      console.error("Delete error:", error)
    },
  }),
)

watch(vendorW9Request.error, (newVal) => {
  console.error("Request info error:", newVal)
})

const handleFileClick = () => {
  fileInput.value.click()
}

const handleFileSelect = (event) => {
  const file = event.target.files[0]
  if (file) {
    processFile(file)
  }

  event.target.value = ""
}

const handleFileDrop = (event) => {
  event.preventDefault()
  const files = event.dataTransfer.files
  if (files.length > 0) {
    processFile(files[0])
  }
}

const processFile = (file) => {
  selectedFile.value = file

  form.value.document = file
  form.value.setAsFile("document")
  uploadFile()
}

const uploadFile = async () => {
  try {
    await uploadW9Mutation.mutateAsync()
    new Confetti().fireworks(3)
  } catch (error) {
    console.error("Upload error:", error)
  }
}

const handleDeleteDocument = async () => {
  confirm({
    title: "Delete W-9 Document",
    text: "Are you sure you want to delete this W-9 document? This action cannot be undone.",
    iconClass: "text-red-500",
    onConfirm: async () => {
      try {
        await deleteAttachmentMutation.mutateAsync(attachment.value.id)
      } catch (error) {
        console.error("Delete error:", error)
        const errorMessage =
          error.response?.data?.message ?? "Unable to delete document. Please try refreshing the page first."
        alert({
          title: "Alert",
          text: errorMessage,
        })
      }
    },
  })
}
</script>
